import React, { useCallback, useState, createContext, useContext } from 'react'
import PropTypes from 'prop-types'

const StoreContext = createContext()

const propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.shape()
  ]).isRequired
}

export const StoreContextProvider = ({ children }) => {
  const [alertDescriptions, setAlertDescriptions] = useState(null)
  const [areAlertDescriptionsLoaded, setAreAlertDescriptionsLoaded] = useState(false)

  const [alertNotificationConfigurations, setAlertNotificationConfigurations] = useState(null)
  const [alertNotificationConfigurationsCollection, setAlertNotificationConfigurationsCollection] = useState(null)
  const [areAlertNotificationConfigurationsLoaded, setAreAlertNotificationConfigurationsLoaded] = useState(false)

  const [archivedDevices, setArchivedDevices] = useState(null)
  const [areArchivedDevicesLoaded, setAreArchivedDevicesLoaded] = useState(false)
  const [isArchivedDevicesLoaded, setIsArchivedDevicesLoaded] = useState(false)

  const [unassignedDevices, setUnassignedDevices] = useState(null)
  const [areUnassignedDevicesLoaded, setAreUnassignedDevicesLoaded] = useState(false)

  const [configs, setConfigs] = useState(null)
  const [areConfigsLoaded, setAreConfigsLoaded] = useState(false)

  const [consolidatedEvents, setConsolidatedEvents] = useState(null)
  const [areConsolidatedEventsLoaded, setAreConsolidatedEventsLoaded] = useState(false)

  const [customers, setCustomers] = useState(null)
  const [arePagedCustomersLoaded, setArePagedCustomersLoaded] = useState(false)
  const [customersMetadata, setCustomersMetadata] = useState(null)
  const [customersCollection, setCustomersCollection] = useState(null)
  const [areCustomersLoaded, setAreCustomersLoaded] = useState(false)

  const [favorites, setFavorites] = useState(null)
  const [areFavoritesLoaded, setAreFavoritesLoaded] = useState(null) // TODO: should be false, not null

  const [faceplateMessages, setFaceplateMessages] = useState(null)
  const [areFaceplateMessagesLoaded, setAreFaceplateMessagesLoaded] = useState(false)

  const [defaultFirmwareLoads, setDefaultFirmwareLoads] = useState(null)
  const [areDefaultFirmwareLoadsLoaded, setAreDefaultFirmwareLoadsLoaded] = useState(false)

  const [deviceMetrics, setDeviceMetrics] = useState(null)
  const [areDeviceMetricsLoaded, setAreDeviceMetricsLoaded] = useState(false)

  const [trends, setTrends] = useState(null)
  const [areTrendsLoaded, setAreTrendsLoaded] = useState(false)

  const [notificationRecipients, setNotificationRecipients] = useState(null)
  const [areNotificationRecipientsLoaded, setAreNotificationRecipientsLoaded] = useState(false)

  const [siteMessages, setSiteMessages] = useState(null)
  const [areSiteMessagesLoaded, setAreSiteMessagesLoaded] = useState(false)

  const [homeownerAlerts, setHomeownerAlerts] = useState < null > (null)
  const [areHomeownerAlertsLoaded, setAreHomeownerAlertsLoaded] = useState(false)

  const [verificationReports, setVerificationReports] = useState(null)
  const [areVerificationReportsLoaded, setAreVerificationReportsLoaded] = useState(false)

  const [systemReports, setSystemReports] = useState(null)
  const [areSystemReportsLoaded, setAreSystemReportsLoaded] = useState(false)

  const [reportCache] = useState({})
  const [devicesCache] = useState({})

  const resetDealerScopedData = useCallback(() => {
    setAreArchivedDevicesLoaded(false)
    setAreCustomersLoaded(false)
    setIsArchivedDevicesLoaded(false)
    setAreUnassignedDevicesLoaded(false)
    setAreNotificationRecipientsLoaded(false)
  }, [
    setAreArchivedDevicesLoaded,
    setIsArchivedDevicesLoaded,
    setAreUnassignedDevicesLoaded,
    setAreCustomersLoaded,
    setAreNotificationRecipientsLoaded
  ])

  return (
    <StoreContext.Provider value={{
      alertDescriptions,
      setAlertDescriptions,
      areAlertDescriptionsLoaded,
      setAreAlertDescriptionsLoaded,

      alertNotificationConfigurations,
      setAlertNotificationConfigurations,
      alertNotificationConfigurationsCollection,
      setAlertNotificationConfigurationsCollection,
      areAlertNotificationConfigurationsLoaded,
      setAreAlertNotificationConfigurationsLoaded,

      archivedDevices,
      setArchivedDevices,
      areArchivedDevicesLoaded,
      setAreArchivedDevicesLoaded,
      isArchivedDevicesLoaded,
      setIsArchivedDevicesLoaded,
      unassignedDevices,
      setUnassignedDevices,
      areUnassignedDevicesLoaded,
      setAreUnassignedDevicesLoaded,

      configs,
      setConfigs,
      areConfigsLoaded,
      setAreConfigsLoaded,

      consolidatedEvents,
      setConsolidatedEvents,
      areConsolidatedEventsLoaded,
      setAreConsolidatedEventsLoaded,

      customers,
      setCustomers,
      arePagedCustomersLoaded,
      setArePagedCustomersLoaded,
      customersMetadata,
      setCustomersMetadata,
      customersCollection,
      setCustomersCollection,
      areCustomersLoaded,
      setAreCustomersLoaded,

      devicesCache,

      favorites,
      setFavorites,
      areFavoritesLoaded,
      setAreFavoritesLoaded,

      faceplateMessages,
      setFaceplateMessages,
      areFaceplateMessagesLoaded,
      setAreFaceplateMessagesLoaded,

      defaultFirmwareLoads,
      setDefaultFirmwareLoads,
      areDefaultFirmwareLoadsLoaded,
      setAreDefaultFirmwareLoadsLoaded,

      deviceMetrics,
      setDeviceMetrics,
      areDeviceMetricsLoaded,
      setAreDeviceMetricsLoaded,

      trends,
      setTrends,
      areTrendsLoaded,
      setAreTrendsLoaded,

      notificationRecipients,
      setNotificationRecipients,
      areNotificationRecipientsLoaded,
      setAreNotificationRecipientsLoaded,

      siteMessages,
      setSiteMessages,
      areSiteMessagesLoaded,
      setAreSiteMessagesLoaded,

      homeownerAlerts,
      setHomeownerAlerts,
      areHomeownerAlertsLoaded,
      setAreHomeownerAlertsLoaded,

      verificationReports,
      setVerificationReports,
      areVerificationReportsLoaded,
      setAreVerificationReportsLoaded,

      systemReports,
      setSystemReports,
      areSystemReportsLoaded,
      setAreSystemReportsLoaded,

      reportCache,

      resetDealerScopedData,
    }}
    >
      {children}
    </StoreContext.Provider>
  )
}

StoreContextProvider.propTypes = propTypes

export const useStoreContext = () => useContext(StoreContext)
